import { useEffect, useRef } from 'react';
import { postMessage } from '../helpers/utils';
import { useSimulationContext } from '../providers/SimulationProvider';
import { MESSAGE_TYPES } from '../constants/simulationConstants';

/**
 * Hook that handles initialization polling by sending INITIALIZE messages
 * to the parent application. The parent responds with data containing
 * character IDs and environment ID, which are then used to render
 * characters and environment in the scene.
 *
 * Polls every 1 second until isClientInitialized or timeout (30 seconds)
 */
function useInitPolling() {
  const intervalRef = useRef(null);
  const counterRef = useRef(0);
  const { isClientInitialized, simulationType, nudgytSessionToken } =
    useSimulationContext();

  useEffect(() => {
    // Cleanup function to clear interval on unmount
    const cleanup = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        counterRef.current = 0;
      }
    };

    if (!simulationType) {
      // Clear any existing interval before starting a new one
      cleanup();

      // Reset counter for new polling cycle
      counterRef.current = 0;

      // Start polling: send INITIALIZE message every 1 second
      intervalRef.current = setInterval(() => {
        counterRef.current += 1;

        // Timeout after 30 seconds (30 attempts)
        if (counterRef.current >= 30) {
          cleanup();
          if (!simulationType || !nudgytSessionToken)
            postMessage({
              type: MESSAGE_TYPES.CHAT,
              error: 'Connection failed'
            });
          console.error(
            '[Init Polling Error] Initialization timeout: No response after 30 seconds'
          );
          return;
        }

        // Send INITIALIZE message to parent application
        // Parent will respond with characterIds and environmentId
        postMessage({ type: MESSAGE_TYPES.INITIALIZE });
      }, 1000);
    } else {
      // Stop polling once initialized
      cleanup();
    }

    return cleanup;
  }, [isClientInitialized, simulationType, nudgytSessionToken]);
}

export default useInitPolling;
